// A card for one chart (revamp PR 5): title, description, meta and actions in the head, the chart in the body, an
// optional footer, and the chart's states in place of the body. Recharts-free: put the chart itself in a lazily
// loaded component and pass it as children.
//
//   <ChartCard
//     title={t('lumen.dashboard.userPerformance')}
//     description={…}                                    // optional, under the title
//     meta={<Badge>{t('lumen.dashboard.topN').replace('{n}', n)}</Badge>}   // beside the title
//     actions={<Button size="sm" variant="outline">…</Button>}             // right side of the head
//     status={rows.length === 0 ? { kind: 'empty', text: t('…noData') } : null}
//     refetching={isFetching && hasData}                 // dims the last chart, shows "Updating…", aria-busy
//     footer={<StatGrid variant="inline">…</StatGrid>}  // under the body, after a hairline
//   >
//     <UsageAreaChart rows={rows} />
//   </ChartCard>
//
// States (decision #15, "refetch keeps the page"):
// - `status.kind === 'loading'`: the first load, nothing to show yet; a spinner and text in the body.
// - `status.kind === 'error' | 'empty'`: the text in the body, no chart. The footer hides too.
// - `refetching`: the last chart and its footer stay, at reduced opacity, with an "Updating…" note in the head. No
//   skeleton, no layout jump. Pass the last rows yourself: a hook's `data` is undefined while a new query key loads.
// `minBodyHeight` keeps the body the chart's height in every state, so a state change doesn't move the page.
import { Loader2 } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { useId } from 'react';

import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { Dimmed } from './dimmed';
import { PageState, type PageStateKind } from './page-state';

export interface ChartCardStatus {
  kind: PageStateKind;
  /** The line shown in place of the chart. */
  text: ReactNode;
}

export interface ChartCardProps {
  title: ReactNode;
  description?: ReactNode;
  /** Beside the title: a count badge, a period. */
  meta?: ReactNode;
  /** The right side of the head: buttons, a menu. Filters that scope the whole page don't belong here. */
  actions?: ReactNode;
  /** Shown in place of the body (and the footer). `null` or absent: the chart. */
  status?: ChartCardStatus | null;
  /** A refetch is running and the children are the last data: dim them and say so (`lumen.chart.updating`). */
  refetching?: boolean;
  footer?: ReactNode;
  /** The body's minimum height in px, in every state (default 256). */
  minBodyHeight?: number;
  className?: string;
  bodyClassName?: string;
  children?: ReactNode;
}

export function ChartCard({
  title,
  description,
  meta,
  actions,
  status,
  refetching = false,
  footer,
  minBodyHeight = 256,
  className,
  bodyClassName,
  children,
}: ChartCardProps) {
  const { t } = useLanguage();
  const titleId = useId();
  const bodyStyle = { minHeight: minBodyHeight } as CSSProperties;

  return (
    <section
      data-slot="chart-card"
      aria-labelledby={titleId}
      aria-busy={refetching || status?.kind === 'loading' || undefined}
      className={cn('flex min-w-0 flex-col rounded-card border border-line bg-raised text-fg', className)}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-5 pt-4">
        <div className="grid min-w-0 gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 id={titleId} className="font-semibold text-fg text-sm leading-5">
              {title}
            </h2>
            {meta}
          </div>
          {description && <p className="text-13 text-fg-muted">{description}</p>}
        </div>
        {(refetching || actions) && (
          <div className="flex shrink-0 items-center gap-2">
            {refetching && (
              <span role="status" className="inline-flex items-center gap-1.5 text-fg-muted text-xs">
                <Loader2 aria-hidden className="size-3.5 animate-spin motion-reduce:animate-none" />
                {t('lumen.chart.updating')}
              </span>
            )}
            {actions}
          </div>
        )}
      </div>

      {status ? (
        <PageState
          kind={status.kind}
          title={status.text}
          framed={false}
          className="min-h-0 px-5 py-6"
          style={bodyStyle}
        />
      ) : (
        <Dimmed
          dimmed={refetching}
          data-slot="chart-card-body"
          className={cn('min-w-0 px-5 pt-3 pb-5', bodyClassName)}
          style={bodyStyle}
        >
          {children}
        </Dimmed>
      )}

      {/* The footer is the last data too: it dims with the body, under a hairline that stays. */}
      {footer && !status && (
        <div className="border-line border-t px-5 py-4">
          <Dimmed dimmed={refetching}>{footer}</Dimmed>
        </div>
      )}
    </section>
  );
}
